import * as yup from 'yup';
import { useEffect, useState } from 'react';
import { MuiFileInput } from 'mui-file-input';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Grid, LinearProgress, Paper, Typography } from '@mui/material';

import { DetailTool } from '../../shared/components';
import { Alerts } from '../../shared/components/alerts';
import { IEstablishment } from '../../shared/protocolos';
import { LayoutBaseDePagina } from '../../shared/layouts';
import { AutoCompleteUser } from './componets/AutoCompleteUser';
import { useAuthContext } from '../../shared/contexts/AuthContext';
import { VTextField, VForm, useVForm, IVFormErrors } from '../../shared/forms';
import { AutoCompleteIsFullStation } from './componets/AutoCompleteIsFullStation';
import { EstablishmentService } from '../../shared/services/establishment/establishmentService';

const formValidationSchema = (isNew: boolean): yup.Schema<any> =>
    yup.object().shape({
        name: yup.string().required().min(3),
        cnpj: yup.string().required().min(14),
        telefone: yup.string().required().min(9),
        nomeFantasia: yup.string().required().min(3),
        razaoSocial: yup.string().required().min(3),
        endereco: yup.string().required().min(3),
        bairro: yup.string().required().min(3),
        cep: yup.string().required().min(8),
        cidade: yup.string().required().min(3),
        uf: yup.string().required().min(2),
        url: yup.string().required(),
        isFullStation: yup.boolean().required(),
        userId: yup.string().required(),
        createdBy: isNew ? yup.string().required() : yup.string().notRequired(),
    });

export const UpsertEstablishment: React.FC = () => {
    const navigate = useNavigate();
    const { id = 'nova' } = useParams<'id'>();
    const userId = localStorage.getItem('LOGGED_USER_ID');
    const { formRef, save, saveAndClose, isSaveAndClose } = useVForm();

    const [alertType, setAlertType] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState<string>('');
    const [alertMessage, setAlertMessage] = useState('');
    const [currentPhotographName, setCurrentPhotographName] = useState<string>('');

    const { userLogged } = useAuthContext();

    useEffect(() => {
        if (id !== 'nova') {
            setIsLoading(true);

            EstablishmentService.getById(id).then((result) => {
                setIsLoading(false);

                if (result instanceof Error) {
                    alert(result.message);
                    navigate('/establishment');
                    return;
                }

                setCurrentPhotographName(result.photograph || '');
                setFileName(result.photograph || '');

                formRef.current?.setData({
                    ...result,
                    fullStation: result.isFullStation ? 'SIM' : 'NÃO',
                });
            });

            return;
        }

        formRef.current?.setData({
            name: '',
            cnpj: '',
            telefone: '',
            nomeFantasia: '',
            razaoSocial: '',
            endereco: '',
            bairro: '',
            cep: '',
            cidade: '',
            uf: '',
            url: '',
            userId: '',
            createdBy: '',
            photograph: '',
            photographPath: '',
            fullStation: '',
        });
    }, [id]);

    const handleChange = (newValue: File | null, selectedFileName: string | undefined) => {
        setFile(newValue);

        if (!selectedFileName) {
            setFileName(currentPhotographName || '');
            return;
        }

        setFileName(selectedFileName);
    };

    const handleSave = async (dados: IEstablishment) => {
        const isFullStation = dados.fullStation === 'SIM';

        const payload = {
            name: dados.name,
            cnpj: dados.cnpj,
            telefone: dados.telefone,
            nomeFantasia: dados.nomeFantasia,
            razaoSocial: dados.razaoSocial,
            endereco: dados.endereco,
            bairro: dados.bairro,
            cep: dados.cep,
            cidade: dados.cidade,
            uf: dados.uf,
            url: dados.url,
            isFullStation,
            userId: dados.userId,
            ...(id === 'nova' ? { createdBy: userLogged.id } : {}),
        };

        if (id === 'nova' && !file) {
            setAlertMessage('Nenhum arquivo selecionado');
            setAlertType(2);
            return;
        }

        formValidationSchema(id === 'nova')
            .validate(payload, { abortEarly: false })
            .then(async (dadosValidados) => {
                setIsLoading(true);

                const result =
                    id === 'nova'
                        ? await EstablishmentService.createWithImage(dadosValidados as IEstablishment, file as File)
                        : await EstablishmentService.updateWithImage(id, dadosValidados, file);

                setIsLoading(false);

                if (result instanceof Error) {
                    setAlertMessage(result.message);
                    setAlertType(1);
                    return;
                }

                setAlertMessage(
                    id === 'nova'
                        ? 'Estabelecimento cadastrado com sucesso'
                        : 'Estabelecimento atualizado com sucesso'
                );
                setAlertType(4);

                if (isSaveAndClose()) {
                    navigate('/establishment');
                    return;
                }

                if (id === 'nova') {
                    navigate(`/establishment/detalhe/${result.id}`);
                }
            })
            .catch((errors: yup.ValidationError) => {
                setIsLoading(false);
                setAlertMessage('Campos inválidos');
                setAlertType(2);

                const validationErrors: IVFormErrors = {};

                errors.inner.forEach((error) => {
                    if (!error.path) return;
                    validationErrors[error.path] = error.message;
                });

                formRef.current?.setErrors(validationErrors);
            });
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Realmente deseja apagar?')) return;

        setIsLoading(true);

        EstablishmentService.deleteById(id, userId as string).then((result) => {
            setIsLoading(false);

            if (result instanceof Error) {
                alert(result.message);
                return;
            }

            alert('Registro apagado com sucesso!');
            navigate('/establishment');
        });
    };

    return (
        <>
            {alertType !== 0 && (
                <Box position="fixed" top="50px" right="10px">
                    <Alerts mensagem={alertMessage} type={alertType} />
                </Box>
            )}

            <LayoutBaseDePagina
                barraDeFerramentas={
                    <DetailTool
                        textoBotaoNovo="Nova"
                        mostrarBotaoSalvarEFechar
                        mostrarBotaoNovo={id !== 'nova'}
                        mostrarBotaoApagar={id !== 'nova'}
                        aoClicarEmSalvar={save}
                        aoClicarEmSalvarEFechar={saveAndClose}
                        aoClicarEmApagar={() => handleDelete(id)}
                        aoClicarEmVoltar={() => navigate('/establishment')}
                        aoClicarEmNovo={() => navigate('/establishment/detalhe/nova')}
                    />
                }
            >
                <VForm ref={formRef} onSubmit={handleSave}>
                    <Box margin={1} component={Paper} variant="outlined" padding={2}>
                        {isLoading && <LinearProgress variant="indeterminate" />}

                        <Typography variant="h6" gutterBottom>
                            {id === 'nova' ? 'Adicionar Estabelecimento' : 'Atualizar Estabelecimento'}
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField fullWidth name="name" label="Nome" disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField fullWidth name="cnpj" label="CNPJ" disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField fullWidth name="telefone" label="Telefone" disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField fullWidth label="Nome Fantasia" name="nomeFantasia" disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField fullWidth name="razaoSocial" label="Razão Social" disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField fullWidth name="endereco" label="Endereço" disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField fullWidth name="bairro" label="Bairro" disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField fullWidth name="cep" label="CEP" disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField fullWidth name="uf" label="UF" disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField fullWidth name="cidade" label="Cidade" disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField fullWidth name="url" label="Link" disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <AutoCompleteIsFullStation />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <MuiFileInput
                                    name="photograph"
                                    placeholder="Foto"
                                    value={file}
                                    disabled={isLoading}
                                    onChange={(newFile) => handleChange(newFile, newFile?.name)}
                                />

                                {id !== 'nova' && currentPhotographName && !file && (
                                    <Typography variant="caption">
                                        Imagem atual: {currentPhotographName}
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <AutoCompleteUser />
                            </Grid>
                        </Grid>
                    </Box>
                </VForm>
            </LayoutBaseDePagina>
        </>
    );
};